/** eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  SendHorizonal,
  ThumbsUp,
  ThumbsDown,
  Copy,
  RotateCcw,
  Globe,
  Mic,
  ExternalLink,
  MessageCircle,
  Trash2,
  Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import api from "../../http";
import { useAuth } from "@workos-inc/authkit-react";
import { useLocation } from "react-router";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./Assistant.css";
import { useToast } from "@/hooks/use-toast";
import { CommentModal } from "./CommentModal";
import SettingsDropdown from "./SettingsDropdown";
import ProfileImageModal from "./ProfileImageModal";
import { useAssistantAvatar } from "../../contexts/AssistantAvatarContext";
import { APP_NAME, appConfig, welcomeMessageTemplate } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const createInitialMsg = (assistantName?: string, template?: string) => ({
  id: "initial",
  role: "assistant" as "user" | "assistant",
  content: (template ?? welcomeMessageTemplate).replace(
    "{ASSISTANT_NAME}",
    assistantName || appConfig.assistantName
  ),
  timestamp: new Date(),
});

type IconActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tooltip: React.ReactNode;
};

const IconActionButton = ({
  tooltip,
  className,
  children,
  ...props
}: IconActionButtonProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          {...props}
          className={cn(
            "p-1 lg:p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none",
            className,
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[320px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  threadId?: string;
  openaiMsgId?: string;
  feedback?: "liked" | "disliked" | null;
  timestamp: Date;
  loading?: boolean;
  comment?: string | null;
  user?: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null;
}

interface Source {
  name: string;
  url: string;
}

interface SSE {
  type: "stream_started" | "stream_ended" | "text_generation" | "loading_state_change";
  code: number;
  isFinished: boolean;
  data: {
    threadId?: string;
    openaiMsgId?: string;
    messageId?: string;
    content?: string;
    timestamp?: Date;
    loadingText?: string;
  };
}

// Function to extract sources from message content
const extractSources = (
  content: string,
): { cleanContent: string; sources: Source[] } => {
  const sources: Source[] = [];

  // Pattern to match sources section and extract sources
  const sourcesRegex = /\*\*(?:Sources|Quellen)\*\*\s*([\s\S]*?)(?=\n\n|\n*$)/i;
  const sourcesMatch = content.match(sourcesRegex);

  if (sourcesMatch) {
    const sourcesSection = sourcesMatch[1];

    // Extract individual source links: [📄 filename](url)
    const sourceRegex = /\[📄\s*([^\]]+)\]\(([^)]+)\)/g;
    let sourceMatch;

    while ((sourceMatch = sourceRegex.exec(sourcesSection)) !== null) {
      const name = sourceMatch[1].trim();
      const url = sourceMatch[2].trim();

      // Only add valid sources
      if (name && url) {
        sources.push({ name, url });
      }
    }

    // Remove the entire sources section from content
    const cleanContent = content.replace(sourcesRegex, "").trim();
    return { cleanContent, sources };
  }

  return { cleanContent: content, sources };
};

// Component for displaying source buttons
const SourceButtons = ({ sources }: { sources: Source[] }) => {
  if (sources.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {sources.map((source, index) => (
        <Button
          key={index}
          variant="outline"
          size="sm"
          className="rounded-full text-xs px-3 py-1 h-7 bg-white hover:bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-800 transition-colors"
          onClick={() =>
            window.open(source.url, "_blank", "noopener,noreferrer")
          }
        >
          <span className="mr-1">📄</span>
          <span className="truncate max-w-[200px]">{source.name}</span>
          <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
        </Button>
      ))}
    </div>
  );
};

function Assistant() {
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  const { getAccessToken, user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const { assistantAvatarUrl, currentAssistant, isLoadingPhoto } =
    useAssistantAvatar();
  const assistantLabel = currentAssistant?.name || appConfig.assistantName;

  // Add a key that changes when the URL changes
  const urlKey = location.search;

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("...");
  const [searchMode, setSearchMode] = useState<"rag" | "web">("rag");

  const [threadId, setThreadId] = useState("");
  const [messages, setMessages] = useState<Message[]>([createInitialMsg()]);

  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  const getWelcomeTemplate = () =>
    currentAssistant?.welcome_message || welcomeMessageTemplate;

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Comment modal state
  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean;
    messageId: string | null;
    existingComment: string | null;
  }>({
    isOpen: false,
    messageId: null,
    existingComment: null,
  });
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [pendingDownvoteMessageId, setPendingDownvoteMessageId] = useState<
    string | null
  >(null);

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Browser compatibility utilities
  const getSupportedMimeType = (): string | null => {
    const mimeTypes = [
      "audio/webm;codecs=opus",
      "audio/webm;codecs=vorbis",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/ogg;codecs=vorbis",
      "audio/wav",
    ];

    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return null;
  };

  const isMediaRecorderSupported = (): boolean => {
    return (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function"
    );
  };

  const isGetUserMediaSupported = (): boolean => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  const getAudioConstraints = () => {
    // Start with basic audio constraints
    const constraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };

    // Only add deviceId if we're not on Safari (which can be problematic)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) {
      constraints.deviceId = { ideal: "default" };
    }

    return constraints;
  };

  const isMicrophoneSupported = (): boolean => {
    return isGetUserMediaSupported() && isMediaRecorderSupported();
  };

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Update welcome message when currentAssistant changes (for initial state)
  useEffect(() => {
    setMessages((prev) => {
      // Only update if we're on the initial message
      if (prev.length === 1 && prev[0].id === "initial") {
        return [createInitialMsg(currentAssistant?.name, getWelcomeTemplate())];
      }
      return prev;
    });
  }, [currentAssistant?.name, currentAssistant?.welcome_message]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadParam = params.get("thread");
    const isNewChat = params.get("new") === "true";

    if (isNewChat) {
      setThreadId("");
      setMessages([createInitialMsg(currentAssistant?.name, getWelcomeTemplate())]);
    } else if (threadParam) {
      loadThreadMessages(threadParam);
    }
  }, [location.search, urlKey, currentAssistant?.name]);

  const loadThreadMessages = async (tId: string) => {
    try {
      setIsLoadingThread(true);
      const token = await getAccessToken();
      const response = await api.getThreadMessages(tId, token);

      setThreadId(response.messages[0]?.thread_id);

      const formattedMessages: Message[] = response.messages.map((msg: any) => {
        return {
          id: msg.id,
          role: msg.role,
          content: msg.content,
          threadId: msg.thread_id,
          timestamp: new Date(msg.created_at),
          comment: msg.comment,
          feedback: msg.feedback,
          user: msg.user,
        };
      });

      setMessages(formattedMessages);
    } catch (error) {
      console.error("Failed to load thread messages:", error);
      setThreadId("");
      setMessages([createInitialMsg(currentAssistant?.name, getWelcomeTemplate())]);
    } finally {
      setIsLoadingThread(false);
    }
  };

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
      // Also update any user messages in the current thread
      setMessages((prev) =>
        prev.map((msg) =>
          msg.role === "user" && msg.user
            ? { ...msg, user: { ...msg.user, profile_image_url: imageUrl } }
            : msg,
        ),
      );
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
    // Also update any user messages in the current thread
    setMessages((prev) =>
      prev.map((msg) =>
        msg.role === "user" && msg.user
          ? {
              ...msg,
              user: {
                ...msg.user,
                first_name: updatedUser.first_name,
                last_name: updatedUser.last_name,
              },
            }
          : msg,
      ),
    );
  };

  const getUserAvatar = (message: Message) => {
    if (message.role === "user") {
      // For user messages, use current user info or message user info
      const userInfo = message.user || currentUser;
      const initials = userInfo
        ? `${userInfo.first_name?.charAt(0) || ""}${
            userInfo.last_name?.charAt(0) || ""
          }`.toUpperCase() || "U"
        : "U";

      return (
        <Avatar className="w-12 h-12">
          <AvatarImage
            src={userInfo?.profile_image_url || undefined}
            alt="User avatar"
          />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
      );
    } else {
      // For assistant messages
      return (
        <Avatar className="w-12 h-12">
          {isLoadingPhoto ? (
            <AvatarFallback className="animate-pulse">
              <Bot className="h-6 w-6 text-gray-400" />
            </AvatarFallback>
          ) : (
            <>
              <AvatarImage
                src={assistantAvatarUrl}
                alt={assistantLabel}
                className="object-cover scale-100"
              />
              <AvatarFallback>
                <Bot className="h-6 w-6 text-red-500" />
              </AvatarFallback>
            </>
          )}
        </Avatar>
      );
    }
  };

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream
          ?.getTracks()
          .forEach((track) => track.stop());
      }
    };
  }, [isRecording]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage: Message = {
      id: uuidv4(),
      role: "user",
      content: trimmed,
      timestamp: new Date(),
      threadId: threadId,
      user: currentUser,
    };

    const assistantLoadingId = `assistant-loading-${Date.now()}`;
    const placeholderAssistantMessage: Message = {
      id: assistantLoadingId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      loading: true,
    };

    setMessages((prevMes) => [
      ...prevMes,
      userMessage,
      placeholderAssistantMessage,
    ]);
    setInput("");
    setLoadingText("...");
    setIsLoading(true);

    try {
      const token = await getAccessToken();

      if (searchMode === "web") {
        // Use web search API
        await api.askWebSearch(
          trimmed,
          userMessage.id,
          threadId,
          (sseEvent) => {
            try {
              const sseMsg: SSE = JSON.parse(sseEvent);
              if (sseMsg.type === "loading_state_change") {
                setLoadingText(sseMsg.data.loadingText || "...");
                return;
              }
              if (sseMsg.type === "stream_ended" || sseMsg.isFinished) {
                console.log("Stream ended, setting isLoading to false");
                setIsLoading(false);
                textareaRef.current?.focus();
              }
              if (!sseMsg.data.content) return;

              setMessages((prevMessages) => {
                const msgIdx = prevMessages.findIndex(
                  (m) => m.id === sseMsg.data.messageId,
                );
                if (msgIdx !== -1) {
                  const updated = [...prevMessages];
                  updated[msgIdx] = {
                    ...updated[msgIdx],
                    content: updated[msgIdx].content + (sseMsg.data.content || ''),
                  };
                  return updated;
                } else {
                  const placeholderIdx = prevMessages.findIndex(
                    (m) => m.loading === true && m.role === "assistant",
                  );
                  if (placeholderIdx !== -1) {
                    const updated = [...prevMessages];
                    updated[placeholderIdx] = {
                      ...updated[placeholderIdx],
                      id: sseMsg.data.messageId || '',
                      loading: false,
                      threadId: sseMsg.data.threadId || '',
                      openaiMsgId: sseMsg.data.openaiMsgId || '',
                      content:
                        updated[placeholderIdx].content + sseMsg.data.content,
                    };
                    setThreadId(sseMsg.data.threadId || '');
                    return updated;
                  } else {
                    const newMsg: Message = {
                      id: sseMsg.data.messageId || '',
                      role: "assistant",
                      content: sseMsg.data.content || '',
                      timestamp: new Date(),
                      threadId: sseMsg.data.threadId || '',
                      openaiMsgId: sseMsg.data.openaiMsgId || '',
                    };
                    setThreadId(sseMsg.data.threadId || '');
                    return [...prevMessages, newMsg];
                  }
                }
              });
            } catch (err) {
              console.error("failed to parse SSE message:", err);
            }
          },
          token,
        );
      } else {
        // Use existing RAG API
        await api.ask(
          trimmed,
          userMessage.id,
          threadId,
          (sseEvent) => {
            try {
              const sseMsg: SSE = JSON.parse(sseEvent);
              if (sseMsg.type === "loading_state_change") {
                setLoadingText(sseMsg.data.loadingText || "...");
                return;
              }
              if (sseMsg.type === "stream_ended" || sseMsg.isFinished) {
                console.log("Stream ended, setting isLoading to false");
                setIsLoading(false);
                textareaRef.current?.focus();
              }
              if (!sseMsg.data.content) return;

              setMessages((prevMessages) => {
                const msgIdx = prevMessages.findIndex(
                  (m) => m.id === sseMsg.data.messageId,
                );
                if (msgIdx !== -1) {
                  const updated = [...prevMessages];
                  updated[msgIdx] = {
                    ...updated[msgIdx],
                    content: updated[msgIdx].content + (sseMsg.data.content || ''),
                  };
                  return updated;
                } else {
                  const placeholderIdx = prevMessages.findIndex(
                    (m) => m.loading === true && m.role === "assistant",
                  );
                  if (placeholderIdx !== -1) {
                    const updated = [...prevMessages];
                    updated[placeholderIdx] = {
                      ...updated[placeholderIdx],
                      id: sseMsg.data.messageId || '',
                      loading: false,
                      threadId: sseMsg.data.threadId || '',
                      openaiMsgId: sseMsg.data.openaiMsgId || '',
                      content:
                        updated[placeholderIdx].content + sseMsg.data.content,
                    };
                    setThreadId(sseMsg.data.threadId || '');
                    return updated;
                  } else {
                    const newMsg: Message = {
                      id: sseMsg.data.messageId || '',
                      role: "assistant",
                      content: sseMsg.data.content || '',
                      timestamp: new Date(),
                      threadId: sseMsg.data.threadId || '',
                      openaiMsgId: sseMsg.data.openaiMsgId || '',
                    };
                    setThreadId(sseMsg.data.threadId || '');
                    return [...prevMessages, newMsg];
                  }
                }
              });
            } catch (err) {
              console.error("failed to parse SSE message:", err);
            }
          },
          token,
        );
      }
    } catch (err) {
      console.error("Error sending question:", err);
      setIsLoading(false);
      // Fall back: ensure we show an error message if request itself failed
      setMessages((prev) => {
        const placeholderIdx = prev.findIndex(
          (m) => m.loading === true && m.role === "assistant",
        );
        if (placeholderIdx !== -1) {
          const updated = [...prev];
          updated[placeholderIdx] = {
            ...updated[placeholderIdx],
            loading: false,
            content:
              "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
          };
          return updated;
        }
        return prev;
      });
    } finally {
      // Don't set isLoading to false here - let the SSE stream_ended event handle it
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        handleSubmit(e as any);
      }
    }
  };

  const handleFeedback = async (
    messageId: string,
    feedback: "liked" | "disliked",
  ) => {
    // Find the current message to check existing feedback
    const currentMessage = messages.find(msg => msg.id === messageId);
    const currentFeedback = currentMessage?.feedback;
    
    // If clicking the same feedback button, remove the feedback (toggle off)
    const newFeedback = currentFeedback === feedback ? null : feedback;
    
    console.log("Feedback for message:", messageId, "from", currentFeedback, "to", newFeedback);
    
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId ? { ...msg, feedback: newFeedback } : msg,
      ),
    );
    await sendFeedbackToBackend(messageId, newFeedback);
  };

  const handleDislikeClick = (
    messageId: string,
    existingComment?: string | null,
  ) => {
    const currentMessage = messages.find((msg) => msg.id === messageId);
    const currentFeedback = currentMessage?.feedback;

    if (currentFeedback === "disliked") {
      void handleFeedback(messageId, "disliked");
      return;
    }

    if (existingComment && existingComment.trim()) {
      void handleFeedback(messageId, "disliked");
      return;
    }

    setPendingDownvoteMessageId(messageId);
    handleCommentClick(messageId, existingComment);
  };

  const sendFeedbackToBackend = async (
    messageId: string,
    feedback: "liked" | "disliked" | null,
  ) => {
    try {
      const token = await getAccessToken();
      await api.feedback(messageId, feedback === null ? "none" : feedback, token);
    } catch (error) {
      console.error("Failed to send feedback:", error);
    }
  };

  const handleCommentClick = (
    messageId: string,
    existingComment?: string | null,
  ) => {
    setCommentModal({
      isOpen: true,
      messageId,
      existingComment: existingComment || null,
    });
  };

  const handleCommentSubmit = async (comment: string) => {
    if (!commentModal.messageId) return;
    const messageId = commentModal.messageId;

    try {
      setIsSubmittingComment(true);
      const token = await getAccessToken();
      const updatedMessage = await api.addCommentToMessage(
        messageId,
        comment,
        token,
      );

      // Update the message in state
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? { ...msg, comment: updatedMessage.comment }
            : msg,
        ),
      );

      if (pendingDownvoteMessageId === messageId) {
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId ? { ...msg, feedback: "disliked" } : msg,
          ),
        );
        await sendFeedbackToBackend(messageId, "disliked");
        setPendingDownvoteMessageId(null);
      }

      toast({
        title: "Kommentar erfolgreich gespeichert",
      });

      setCommentModal({
        isOpen: false,
        messageId: null,
        existingComment: null,
      });
    } catch (error) {
      console.error("Failed to save comment:", error);
      toast({
        title: "Kommentar konnte nicht gespeichert werden",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentModalClose = () => {
    if (pendingDownvoteMessageId === commentModal.messageId) {
      setPendingDownvoteMessageId(null);
    }
    setCommentModal({ isOpen: false, messageId: null, existingComment: null });
  };

  const handleDeleteCommentClick = async (messageId: string) => {
    try {
      setIsDeletingComment(true);
      const token = await getAccessToken();
      const updatedMessage = await api.deleteCommentFromMessage(
        messageId,
        token,
      );

      // Update the message in state
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                comment: updatedMessage.comment,
                feedback: msg.feedback === "disliked" ? null : msg.feedback,
              }
            : msg,
        ),
      );

      setCommentModal((prev) =>
        prev.messageId === messageId ? { ...prev, existingComment: null } : prev,
      );

      toast({
        title: "Kommentar erfolgreich gelöscht",
      });
    } catch (error) {
      console.error("Failed to delete comment:", error);
      toast({
        title: "Kommentar konnte nicht gelöscht werden",
        variant: "destructive",
      });
    } finally {
      setIsDeletingComment(false);
    }
  };

  const handleResend = (messageId: string) => {
    const assistantMsgIndex = messages.findIndex((m) => m.id === messageId);
    if (assistantMsgIndex > 0) {
      const userMessage = messages[assistantMsgIndex - 1];
      if (userMessage && userMessage.role === "user") {
        const questionContent = userMessage.content;
        setInput(questionContent);

        setTimeout(() => {
          textareaRef.current?.focus();

          const resendQuestion = async () => {
            if (!questionContent || isLoading) return;
            const newUserMessage: Message = {
              id: uuidv4(),
              role: "user",
              content: questionContent,
              timestamp: new Date(),
              threadId: threadId,
              user: currentUser,
            };

            const assistantLoadingId = `assistant-loading-${Date.now()}`;
            const placeholderAssistantMessage: Message = {
              id: assistantLoadingId,
              role: "assistant",
              content: "",
              timestamp: new Date(),
              loading: true,
            };

            setMessages((prev) => [
              ...prev,
              newUserMessage,
              placeholderAssistantMessage,
            ]);
            setInput("");
            setLoadingText("...");
            setIsLoading(true);

            try {
              const token = await getAccessToken();
              await api.ask(
                questionContent,
                newUserMessage.id,
                threadId,
                (sseEvent) => {
                  try {
                    const sseMsg: SSE = JSON.parse(sseEvent);
                    if (sseMsg.type === "loading_state_change") {
                      setLoadingText(sseMsg.data.loadingText || "...");
                      return;
                    }
                    if (!sseMsg.data.content) return;

                    setMessages((prevMessages) => {
                      const msgIdx = prevMessages.findIndex(
                        (m) => m.id === sseMsg.data.messageId,
                      );
                      if (msgIdx !== -1) {
                        // just append
                        const updated = [...prevMessages];
                        updated[msgIdx] = {
                          ...updated[msgIdx],
                          content:
                            updated[msgIdx].content + sseMsg.data.content,
                        };
                        return updated;
                      } else {
                        const placeholderIdx = prevMessages.findIndex(
                          (m) => m.loading === true && m.role === "assistant",
                        );
                        if (placeholderIdx !== -1) {
                          const updated = [...prevMessages];
                          updated[placeholderIdx] = {
                            ...updated[placeholderIdx],
                            id: sseMsg.data.messageId || '',
                            loading: false,
                            threadId: sseMsg.data.threadId || '',
                            openaiMsgId: sseMsg.data.openaiMsgId || '',
                            content:
                              updated[placeholderIdx].content +
                              sseMsg.data.content,
                          };
                          setThreadId(sseMsg.data.threadId || '');
                          return updated;
                        } else {
                          const newMsg: Message = {
                            id: sseMsg.data.messageId || '',
                            role: "assistant",
                            content: sseMsg.data.content || '',
                            timestamp: new Date(),
                            threadId: sseMsg.data.threadId || '',
                            openaiMsgId: sseMsg.data.openaiMsgId || '',
                          };
                          setThreadId(sseMsg.data.threadId || '');
                          return [...prevMessages, newMsg];
                        }
                      }
                    });
                  } catch (err) {
                    console.error("failed to parse SSE message:", err);
                  }
                },
                token,
                (err) => {
                  console.error("Stream error (resend):", err);
                  setIsLoading(false);
                  setMessages((prev) => {
                    const placeholderIdx = prev.findIndex(
                      (m) => m.loading === true && m.role === "assistant",
                    );
                    if (placeholderIdx !== -1) {
                      const updated = [...prev];
                      updated[placeholderIdx] = {
                        ...updated[placeholderIdx],
                        loading: false,
                        content:
                          "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
                      };
                      return updated;
                    }
                    return [
                      ...prev,
                      {
                        id: `assistant-error-${Date.now()}`,
                        role: "assistant" as const,
                        content:
                          "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
                        timestamp: new Date(),
                      },
                    ];
                  });
                },
              );
            } catch (err) {
              console.error("err", err);
            } finally {
              // Don't set isLoading to false here - let the SSE stream_ended event handle it
            }
          };

          resendQuestion();
        }, 0);
      }
    }
  };


  const autoResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const startRecording = async () => {
    try {
      // Check browser support
      if (!isGetUserMediaSupported()) {
        throw new Error("getUserMedia is not supported in this browser");
      }

      if (!isMediaRecorderSupported()) {
        throw new Error("MediaRecorder is not supported in this browser");
      }

      // Request microphone permission with browser-agnostic configuration
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getAudioConstraints(),
      });

      // Reset audio chunks
      audioChunksRef.current = [];

      // Get supported MIME type for this browser
      const supportedMimeType = getSupportedMimeType();

      // Configure MediaRecorder with browser-specific options
      const options: MediaRecorderOptions = {
        audioBitsPerSecond: 128000,
      };

      if (supportedMimeType) {
        options.mimeType = supportedMimeType;
        console.debug("Using MIME type:", supportedMimeType);
      } else {
        console.warn("No supported MIME type found, using browser default");
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop());
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Failed to start recording:", error);

      // Provide more specific error messages based on the error type
      let errorMessage =
        "Bitte erlauben Sie den Zugriff auf das Mikrofon, um Sprachnachrichten zu senden.";

      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          errorMessage =
            "Mikrofon-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.";
        } else if (error.name === "NotFoundError") {
          errorMessage =
            "Kein Mikrofon gefunden. Bitte stellen Sie sicher, dass ein Mikrofon angeschlossen ist.";
        } else if (error.name === "NotSupportedError") {
          errorMessage =
            "Sprachaufnahme wird in diesem Browser nicht unterstützt.";
        } else if (
          error.message.includes("getUserMedia") ||
          error.message.includes("MediaRecorder")
        ) {
          errorMessage =
            "Sprachaufnahme wird in diesem Browser nicht unterstützt. Bitte verwenden Sie einen modernen Browser.";
        }
      }

      toast({
        title: "Mikrofon-Zugriff fehlgeschlagen",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current || !isRecording) return;

    setIsRecording(false);
    setIsProcessingRecording(true);

    // Stop recording
    mediaRecorderRef.current.stop();

    // Wait for the stop event and then process
    mediaRecorderRef.current.onstop = async () => {
      try {
        // Create blob from chunks using the actual MIME type from MediaRecorder
        const actualMimeType = mediaRecorderRef.current?.mimeType;

        // Fallback to a common MIME type if none is specified
        const fallbackMimeType = actualMimeType || "audio/webm";

        const audioBlob = new Blob(audioChunksRef.current, {
          type: fallbackMimeType,
        });

        console.debug("Created audio blob:", {
          size: audioBlob.size,
          type: audioBlob.type,
          originalMimeType: actualMimeType,
          fallbackMimeType,
        });

        // Validate blob size (prevent sending empty or corrupted recordings)
        if (audioBlob.size === 0) {
          throw new Error("Recording produced empty audio data");
        }

        // Send to transcription API
        const token = await getAccessToken();
        const result = await api.transcribe(audioBlob, token);

        if (result.transcript && result.transcript.trim()) {
          // Populate textarea with transcript
          setInput(result.transcript.trim());

          // Auto-resize textarea
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            }
          }, 0);

          // Focus textarea for immediate editing if needed
          textareaRef.current?.focus();
        } else {
          toast({
            title: "Transkription fehlgeschlagen",
            description: "Die Spracherkennung konnte keinen Text erkennen.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Transcription failed:", error);

        let errorMessage = "Es gab einen Fehler bei der Spracherkennung.";

        if (error instanceof Error) {
          if (error.message.includes("empty audio data")) {
            errorMessage =
              "Die Aufnahme war zu kurz oder leer. Bitte versuchen Sie es erneut.";
          } else if (
            error.message.includes("network") ||
            error.message.includes("fetch")
          ) {
            errorMessage =
              "Netzwerkfehler bei der Spracherkennung. Bitte überprüfen Sie Ihre Internetverbindung.";
          }
        }

        toast({
          title: "Transkription fehlgeschlagen",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsProcessingRecording(false);
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
      }
    };
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else if (!isProcessingRecording) {
      startRecording();
    }
  };

  const feedbackTooltip =
    "Dein Feedback wird vom Team geprüft. Bewertungen helfen uns, falsche Antworten zu erkennen und die zugrunde liegenden Dokumente zu verbessern.";

  return (
    <div className="flex flex-col h-full w-full bg-transparent">
      <div className="flex flex-col flex-1 w-full h-full">
        {/* Header - hidden on mobile */}
        <div className="hidden lg:flex items-center justify-between p-4">
          <h2 className="text-lg font-bold text-slate-800">
            {APP_NAME} Wissensasistent
          </h2>
          <div className="flex items-center gap-4">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1" ref={scrollAreaRef}>
          <div className="w-full px-4 sm:px-10 lg:px-[100px] py-6 pb-32 lg:pb-6 space-y-6">
            {/* If loading thread history */}
            {isLoadingThread ? (
              <div className="flex justify-center items-center h-40">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: APP_COLOR }} />
                  <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: APP_COLOR }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: APP_COLOR }} />
                </div>
              </div>
            ) : (
              <>
                {/* Render each message */}
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-2 lg:gap-4 items-start px-2 lg:px-0",
                      message.role === "user" ? "flex-row-reverse" : "",
                    )}
                  >
                    {/* Avatar */}
                    {getUserAvatar(message)}

                    {/* Content */}
                    <div
                      className={cn(
                        "max-w-[85%] lg:max-w-[70%] relative flex flex-col gap-1 lg:gap-2",
                        message.role === "user" ? "ml-auto" : "mr-auto",
                      )}
                    >
                      {/* Name + Timestamp */}
                      <div className="flex items-center gap-1 lg:gap-2">
                        <span className="font-bold text-xs lg:text-sm text-slate-800">
                          {message.role === "user" ? "You" : assistantLabel}
                        </span>
                        <span className="text-xs text-slate-400">
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>

                      {/* If assistant is in loading state, show custom loading text or bouncing dots. Otherwise, show text */}
                      {message.loading ? (
                        <Card className="bg-[#F8FAFC] text-slate-700 p-2 lg:p-3 rounded-2xl max-w-[200px]">
                          {loadingText === "..." ? (
                            <div className="flex space-x-2 transform translate-y-[0.75px] justify-center">
                              <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: APP_COLOR }} />
                              <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: APP_COLOR }} />
                              <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: APP_COLOR }} />
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-slate-600 animate-pulse">
                              {loadingText}
                            </div>
                          )}
                        </Card>
                      ) : (
                        <>
                          <Card className="md-wrapper overflow-x-auto bg-[#F8FAFC] p-2 lg:p-3 rounded-2xl text-sm lg:text-base font-normal leading-relaxed text-slate-600">
                            <Markdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                a: ({ href, children }) => (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {message.role === "assistant"
                                ? extractSources(message.content).cleanContent
                                : message.content}
                            </Markdown>
                          </Card>
                        </>
                      )}

                      {/* Feedback & Resend (only for assistant's completed messages) */}
                      {message.role === "assistant" &&
                        message.id !== "initial" &&
                        !message.loading && (
                          <>
                            <div className="flex gap-2 lg:gap-4 text-slate-400 mt-1">
                              <IconActionButton
                                onClick={() => {
                                  const { cleanContent } = extractSources(
                                    message.content,
                                  );
                                  navigator.clipboard.writeText(cleanContent);
                                  toast({
                                    title: "Copied to clipboard",
                                  });
                                }}
                                tooltip="Antwort (ohne Quellenabschnitt) in die Zwischenablage kopieren."
                              >
                                <Copy className="w-3 h-3 lg:w-4 lg:h-4" />
                              </IconActionButton>

                              <IconActionButton
                                onClick={() =>
                                  message.id &&
                                  handleFeedback(message.id, "liked")
                                }
                                className={cn(
                                  message.feedback === "liked" &&
                                    "text-blue-500",
                                )}
                                tooltip={feedbackTooltip}
                              >
                                <ThumbsUp className="h-3 w-3 lg:h-4 lg:w-4" />
                              </IconActionButton>

                              <IconActionButton
                                onClick={() =>
                                  message.id &&
                                  handleDislikeClick(message.id, message.comment)
                                }
                                className={cn(
                                  message.feedback === "disliked" &&
                                    "text-red-500",
                                )}
                                tooltip={feedbackTooltip}
                              >
                                <ThumbsDown className="h-3 w-3 lg:h-4 lg:w-4" />
                              </IconActionButton>

                              {/* Comment Button */}
                              <IconActionButton
                                onClick={() =>
                                  handleCommentClick(
                                    message.id,
                                    message.comment,
                                  )
                                }
                                className={cn(
                                  message.comment && "text-blue-500",
                                )}
                                tooltip={
                                  message.comment
                                    ? "Kommentar bearbeiten: Was war unklar, falsch oder hat in der Antwort gefehlt?"
                                    : "Was war unklar, falsch oder hat in der Antwort gefehlt? Dein Hinweis hilft uns, die Dokumentation zu verbessern."
                                }
                              >
                                <MessageCircle className="w-3 h-3 lg:w-4 lg:h-4" />
                              </IconActionButton>

                              <IconActionButton
                                onClick={() => handleResend(message.id)}
                                tooltip="Neue Antwort zur gleichen Frage generieren."
                              >
                                <RotateCcw className="w-3 h-3 lg:w-4 lg:h-4" />
                              </IconActionButton>
                            </div>

                            {/* Display comment if exists */}
                            {message.comment && (
                              <div className="mt-2 p-2 lg:p-3 bg-gray-50 rounded-md border-l-4 border-blue-200">
                                <div className="flex justify-between items-start mb-1 lg:mb-2">
                                  <div className="text-xs text-gray-500 font-medium">
                                    Ihr Kommentar:
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleDeleteCommentClick(message.id)
                                    }
                                    className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-colors"
                                    title="Kommentar löschen"
                                    disabled={isDeletingComment}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className="text-xs lg:text-sm text-gray-700">
                                  {message.comment}
                                </div>
                              </div>
                            )}

                            {/* Sources buttons for assistant messages */}
                            {(() => {
                              const { sources } = extractSources(
                                message.content,
                              );
                              return sources.length > 0 ? (
                                <SourceButtons sources={sources} />
                              ) : null;
                            })()}
                          </>
                        )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Always scroll to the bottom */}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* User Input Box - Desktop */}
        <div className="hidden lg:block px-4 sm:px-10 lg:px-[100px] pt-6 pb-6">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col bg-white rounded-2xl px-4 py-3 shadow-sm border border-slate-200 w-full"
          >
            <Textarea
              ref={textareaRef}
              placeholder={
                searchMode === "web"
                  ? "Durchsuche das Web... (Drücke Enter zum Senden)"
                  : "Frag mich etwas... (Drücke Enter zum Senden)"
              }
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                autoResize(e);
              }}
              onKeyDown={handleKeyDown}
              disabled={false}
              rows={1}
              className="w-full min-h-[24px] h-auto max-h-[300px] resize-none bg-transparent border-none outline-none ring-0 focus:ring-0 focus:outline-none shadow-none text-[16px] leading-[1.6] text-slate-700 p-0 mb-3 placeholder:text-slate-400 focus:border-transparent focus-visible:ring-0 focus-visible:outline-none"
            />

            <div className="flex justify-between items-center mt-1">
              <div className="flex gap-2">
                {appConfig.features.webSearchEnabled ? (
                  <div className="flex gap-2">
                    <div className="flex gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() =>
                              setSearchMode(searchMode === "rag" ? "web" : "rag")
                            }
                            className={`p-2 rounded-full border transition ${
                              searchMode === "web"
                                ? "border-blue-500 bg-blue-50 text-blue-600"
                                : "border-slate-300 bg-transparent text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            <Globe className="w-5 h-5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[320px]">
                          {searchMode === "rag"
                            ? "Zur Websuche wechseln"
                            : "Zur Dokumentensuche wechseln"}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                ) : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleMicClick}
                      disabled={isProcessingRecording || !isMicrophoneSupported()}
                      className={cn(
                        "p-2 rounded-full border transition-all duration-200",
                        isRecording
                          ? "border-red-500 bg-red-50 text-red-600 animate-pulse"
                          : isProcessingRecording || !isMicrophoneSupported()
                            ? "border-slate-300 bg-slate-50 text-slate-400 cursor-not-allowed"
                            : "border-slate-300 bg-transparent text-slate-500 hover:bg-slate-100",
                      )}
                    >
                      {isProcessingRecording ? (
                        <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Mic
                          className={cn(
                            "w-5 h-5",
                            isRecording ? "text-red-600" : "text-[#475569]",
                          )}
                        />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[320px]">
                    {isRecording
                      ? "Aufnahme stoppen"
                      : isProcessingRecording
                        ? "Transkription läuft..."
                        : !isMicrophoneSupported()
                          ? "Sprachaufnahme wird in diesem Browser nicht unterstützt"
                          : "Sprachaufnahme starten"}
                  </TooltipContent>
                </Tooltip>
              </div>
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="text-white rounded-full px-4 h-10 text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none"
                style={{ backgroundColor: APP_COLOR }}
              >
                Fragen
                <SendHorizonal className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>

        {/* Mobile Fixed Input Box */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-transparent px-4 py-3 pb-safe">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-slate-200 w-full"
          >
            <Textarea
              ref={textareaRef}
              placeholder="Frag mich etwas….. (Drücke Enter zum Senden)"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                autoResize(e);
              }}
              onKeyDown={handleKeyDown}
              disabled={false}
              rows={1}
              className="w-full min-h-[24px] h-auto max-h-[300px] resize-none bg-transparent border-none outline-none ring-0 focus:ring-0 focus:outline-none shadow-none text-[16px] leading-[1.6] text-slate-700 p-0 mb-3 placeholder:text-slate-400 focus:border-transparent focus-visible:ring-0 focus-visible:outline-none"
            />

            <div className="flex justify-between items-center mt-1">
              <div className="flex gap-2">
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          setSearchMode(searchMode === "rag" ? "web" : "rag")
                        }
                        className={`p-2 rounded-full border transition ${
                          searchMode === "web"
                            ? "border-blue-500 bg-blue-50 text-blue-600"
                            : "border-slate-300 bg-transparent text-slate-500 hover:bg-slate-100"
                        }`}
                      >
                        <Globe className="w-5 h-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[320px]">
                      {searchMode === "rag"
                        ? "Zur Websuche wechseln"
                        : "Zur Dokumentensuche wechseln"}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleMicClick}
                      disabled={isProcessingRecording || !isMicrophoneSupported()}
                      className={cn(
                        "p-2 rounded-full border transition-all duration-200",
                        isRecording
                          ? "border-red-500 bg-red-50 text-red-600 animate-pulse"
                          : isProcessingRecording || !isMicrophoneSupported()
                            ? "border-slate-300 bg-slate-50 text-slate-400 cursor-not-allowed"
                            : "border-slate-300 bg-transparent text-slate-500 hover:bg-slate-100",
                      )}
                    >
                      {isProcessingRecording ? (
                        <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Mic
                          className={cn(
                            "w-5 h-5",
                            isRecording ? "text-red-600" : "text-[#475569]",
                          )}
                        />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[320px]">
                    {isRecording
                      ? "Aufnahme stoppen"
                      : isProcessingRecording
                        ? "Transkription läuft..."
                        : !isMicrophoneSupported()
                          ? "Sprachaufnahme wird in diesem Browser nicht unterstützt"
                          : "Sprachaufnahme starten"}
                  </TooltipContent>
                </Tooltip>
              </div>
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="text-white rounded-full px-4 h-10 text-sm font-semibold disabled:opacity-100 disabled:pointer-events-none"
                style={{ backgroundColor: APP_COLOR }}
              >
                Fragen
                <SendHorizonal className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModal.isOpen}
        onClose={handleCommentModalClose}
        onSubmit={handleCommentSubmit}
        isLoading={isSubmittingComment}
        existingComment={commentModal.existingComment}
      />

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}

export default Assistant;